import clsx from 'clsx'
import { ReactNode, useRef } from 'react'
import { PolymorphicPropsWithoutRef } from 'react-polymorphic-types'
import { BaseProps } from '../types'
import { AriaButtonProps, useButton } from '@react-aria/button'

export interface IconButtonOwnProps extends BaseProps {
    /**
     * The probably svg node to use as the symbol on the button. The svg should
     * have `stroke="currentColor"` to inherit color from the button
     */
    icon: ReactNode
    children?: never
    /**
     * The title attribute for the button.
     */
    title?: string
}

export type IconButtonProps<
    T extends React.ElementType = typeof DefaultButton,
> = PolymorphicPropsWithoutRef<IconButtonOwnProps, T> & AriaButtonProps<T>

const DefaultButton = 'button'

export const IconButton = <
    TButton extends React.ElementType = typeof DefaultButton,
>({
    icon,
    className,
    as,

    children: _,
    ...props
}: IconButtonProps<TButton>) => {
    const Element = as ?? DefaultButton

    const ref = useRef(null)
    const { buttonProps, isPressed } = useButton(
        { ...props, elementType: Element },
        ref,
    )

    return (
        <Element
            data-pressed={isPressed}
            className={clsx(
                className,
                ' flex h-8 w-8 items-center justify-around rounded-md',
            )}
            {...buttonProps}
            title={props.title}
        >
            {icon}
        </Element>
    )
}
